<template>
  <v-card
    class="accreditation-card mx-auto"
    style="max-width: 1200px"
    elevation="0"
    rounded="xl"
  >
    <!-- Header Section with Gradient Accent -->
    <div class="card-header">
      <div class="" />
      <div class="header-content">
        <div class="badge-wrapper">
          <v-icon size="32" color="indigo">mdi-school-outline</v-icon>
          <div class="badge-text text-indigo">National TVET Authority</div>
        </div>
        <v-icon
          icon="mdi-chevron-right"
          size="28"
          color="indigo"
          class="accent-icon"
        />
      </div>
    </div>

    <v-card-item>
      <v-card-title class="card-title">
        Accreditation & Registration Services
        <div class="title-underline" />
      </v-card-title>

      <v-card-subtitle class="card-subtitle">
        Empowering TVET institutions and trainers through quality assurance and
        professional recognition across Somalia.
      </v-card-subtitle>

      <!-- Registration requirement cards -->
      <v-container fluid class="registration-container pa-0 mt-8">
        <v-row>
          <v-col
            v-for="registration in registrationServices"
            :key="registration.id"
            cols="12"
            class="registration-col"
          >
            <article
              class="registration-card"
              :class="`registration-card--${registration.accent}`"
            >
              <div class="registration-card__glow" />

              <header class="registration-card__header">
                <div class="registration-card__icon">
                  <v-icon size="30" :color="registration.accentColor">
                    {{ registration.icon }}
                  </v-icon>
                </div>
                <div class="registration-card__intro">
                  <div class="registration-card__meta">
                    <v-chip
                      size="x-small"
                      :color="registration.accentColor"
                      variant="tonal"
                      rounded="lg"
                      class="font-weight-bold"
                    >
                      {{ registration.steps.length }}-step application
                    </v-chip>
                    <v-chip
                      size="x-small"
                      color="success"
                      variant="tonal"
                      rounded="lg"
                    >
                      Rolling applications
                    </v-chip>
                  </div>
                  <h3 class="registration-card__title">
                    {{ registration.title }}
                  </h3>
                  <p class="registration-card__description">
                    {{ registration.description }}
                  </p>
                </div>
              </header>

              <div class="registration-card__steps">
                <div
                  v-for="(stepItem, index) in registration.steps"
                  :key="stepItem.title"
                  class="registration-card__step-pill"
                >
                  <span class="registration-card__step-num">{{
                    index + 1
                  }}</span>
                  <span class="registration-card__step-label">{{
                    stepItem.title
                  }}</span>
                </div>
              </div>

              <div class="registration-card__sections">
                <section
                  v-for="section in registration.sections"
                  :key="section.title"
                  class="requirement-section"
                >
                  <div class="requirement-section__head">
                    <v-icon
                      size="18"
                      :color="registration.accentColor"
                      class="mr-2"
                    >
                      {{ section.icon }}
                    </v-icon>
                    <h4 class="requirement-section__title">
                      {{ section.title }}
                    </h4>
                  </div>
                  <ul class="requirement-section__list">
                    <li
                      v-for="field in section.fields"
                      :key="field.label"
                      class="requirement-section__item"
                    >
                      <v-icon
                        size="14"
                        :color="field.required ? 'success' : 'grey'"
                        class="requirement-section__bullet"
                      >
                        {{
                          field.required
                            ? "mdi-circle-small"
                            : "mdi-circle-outline"
                        }}
                      </v-icon>
                      <span class="requirement-section__label">
                        {{ field.label }}
                      </span>
                      <v-chip
                        v-if="field.required"
                        size="x-small"
                        color="error"
                        variant="tonal"
                        class="requirement-section__chip"
                      >
                        Required
                      </v-chip>
                      <v-chip
                        v-else
                        size="x-small"
                        variant="tonal"
                        class="requirement-section__chip"
                      >
                        Optional
                      </v-chip>
                    </li>
                  </ul>
                </section>
              </div>

              <div class="registration-card__documents">
                <div class="registration-card__documents-head">
                  <v-icon size="18" color="indigo" class="mr-2">
                    mdi-file-document-multiple-outline
                  </v-icon>
                  <span>Documents to prepare</span>
                </div>
                <div class="registration-card__doc-chips">
                  <v-chip
                    v-for="doc in registration.documents"
                    :key="doc.label"
                    size="small"
                    color="indigo"
                    :variant="doc.required ? 'tonal' : 'outlined'"
                    rounded="lg"
                    class="registration-card__doc-chip"
                  >
                    <v-icon start size="14">
                      {{ doc.required ? "mdi-file-check" : "mdi-file-outline" }}
                    </v-icon>
                    {{ doc.label }}
                  </v-chip>
                </div>
                <p class="registration-card__file-note">
                  <v-icon size="14" class="mr-1"
                    >mdi-information-outline</v-icon
                  >
                  {{ registration.fileNote }}
                </p>
              </div>

              <footer class="registration-card__footer">
                <v-btn
                  color="indigo"
                  size="large"
                  rounded="xl"
                  variant="flat"
                  class="registration-card__cta"
                  @click="registration.onApply"
                >
                  {{ registration.ctaLabel }}
                  <v-icon end>mdi-arrow-right</v-icon>
                </v-btn>
              </footer>
            </article>
          </v-col>
        </v-row>
      </v-container>

      <!-- Other services -->
      <v-container fluid class="services-container pa-0 mt-4">
        <p class="services-kicker">Additional services</p>
        <v-row no-gutters>
          <v-col
            v-for="service in otherServices"
            :key="service.id"
            cols="12"
            md="6"
            class="service-col"
          >
            <div class="service-item">
              <div class="service-icon-wrapper">
                <v-icon size="28" color="indigo">{{ service.icon }}</v-icon>
              </div>
              <div class="service-content">
                <h3 class="service-title">{{ service.title }}</h3>
                <p class="service-description">{{ service.description }}</p>
                <ul v-if="service.features" class="service-features">
                  <li v-for="(feature, idx) in service.features" :key="idx">
                    <v-icon size="16" color="success">mdi-check-circle</v-icon>
                    <span>{{ feature }}</span>
                  </li>
                </ul>
              </div>
            </div>
          </v-col>
        </v-row>
      </v-container>

      <!-- Statistical Highlights -->
      <div class="stats-row">
        <div class="stat-item">
          <div class="stat-value">200+</div>
          <div class="stat-label">Accredited Institutions</div>
        </div>
        <div class="stat-divider" />
        <div class="stat-item">
          <div class="stat-value">98%</div>
          <div class="stat-label">Satisfaction Rate</div>
        </div>
        <div class="stat-divider" />
        <div class="stat-item">
          <div class="stat-value">24/7</div>
          <div class="stat-label">Online Support</div>
        </div>
      </div>
    </v-card-item>

    <v-divider class="mt-4" />
    <v-card-actions class="pa-4">
      <v-spacer />
      <div class="action-left">
        <v-chip color="indigo" variant="tonal" size="small" rounded="lg">
          Rolling Applications
        </v-chip>
        <span class="deadline-text">No deadline for registration</span>
      </div>
      <!-- <div class="card-actions__buttons">
        <v-btn
          color="indigo"
          size="large"
          rounded="xl"
          elevation="0"
          variant="flat"
          class="px-4"
          @click="goToRegisterTrainerView"
        >
          Register Trainer
          <v-icon end>mdi-arrow-right</v-icon>
        </v-btn>
        <v-btn
          color="indigo"
          size="large"
          rounded="xl"
          elevation="0"
          variant="flat"
          class="px-4"
          @click="goToRegisterInstitutionView"
        >
          Register Institution
          <v-icon end>mdi-arrow-right</v-icon>
        </v-btn>
      </div> -->
    </v-card-actions>
  </v-card>
</template>

<script setup>
import { useRouter } from "vue-router";

const router = useRouter();

const goToRegisterInstitutionView = async () => {
  await router.push("/register-institution");
};

const goToRegisterTrainerView = async () => {
  await router.push("/register-trainer");
};

const registrationServices = [
  {
    id: "institution",
    title: "Institution Registration",
    icon: "mdi-domain",
    accent: "indigo",
    accentColor: "indigo",
    ctaLabel: "Start Institution Application",
    onApply: goToRegisterInstitutionView,
    description:
      "Apply to register your TVET institution with MoLSA for official recognition to operate, deliver training programmes and enrol learners on the national register.",
    fileNote:
      "Upload clear PDF or image scans. Maximum 5 MB per file. Passport photo must be JPG or PNG.",
    steps: [
      { title: "Basic info" },
      { title: "Contact" },
      { title: "Facilities" },
      { title: "Documents" },
    ],
    sections: [
      {
        title: "Step 1 · Basic information",
        icon: "mdi-information-outline",
        fields: [
          { label: "Institution name (English)", required: true },
          { label: "Institution name (Somali)", required: true },
          { label: "Institution type", required: true },
          { label: "Ownership (public, private, etc.)", required: true },
          { label: "Federal member state", required: true },
          { label: "Region", required: true },
          { label: "District", required: true },
          { label: "Street / physical address", required: true },
          { label: "Year established", required: true },
        ],
      },
      {
        title: "Step 2 · Contact person",
        icon: "mdi-account-tie-outline",
        fields: [
          { label: "Full name of representative", required: true },
          { label: "Position / title", required: true },
          { label: "Email address", required: true },
          { label: "Phone number", required: true },
          { label: "Alternate phone", required: false },
          { label: "Postal / physical address", required: true },
        ],
      },
      {
        title: "Step 3 · Facilities & infrastructure",
        icon: "mdi-office-building-outline",
        fields: [
          {
            label:
              "Available facilities (library, labs, workshops, dormitory, etc.)",
            required: true,
          },
          { label: "Number of classrooms", required: false },
          { label: "Number of workshops", required: false },
          { label: "Number of laboratories", required: false },
          { label: "Number of libraries", required: false },
          { label: "Total campus area (m²)", required: false },
          { label: "Campus student capacity", required: false },
          { label: "Additional facilities description", required: false },
        ],
      },
    ],
    documents: [
      {
        label: "Institution profile (history, mission, vision)",
        required: true,
      },
      { label: "Ownership deed (NOOTAAYO / title deed)", required: true },
      { label: "No objection letter", required: false },
      { label: "Contact ID (passport / national ID)", required: true },
      { label: "Contact passport-size photo", required: true },
      { label: "Contact curriculum vitae (CV)", required: true },
      { label: "Declaration of accuracy", required: true },
    ],
  },
  {
    id: "trainer",
    title: "Trainer Registration",
    icon: "mdi-human-male-board-poll",
    accent: "indigo",
    accentColor: "indigo",
    ctaLabel: "Start Trainer Application",
    onApply: goToRegisterTrainerView,
    description:
      "Register as a certified TVET trainer, assessor, or verifier. Showcase your qualifications and professional experience to teach, assess and verify skills on the national TVET register.",
    fileNote:
      "Passport photo: JPG/PNG only (max 5 MB). Curriculum Vitae (CV): max 10 MB. Other documents: PDF or image, max 5 MB each.",
    steps: [
      { title: "Personal" },
      { title: "Location" },
      { title: "Qualifications" },
      { title: "Documents" },
    ],
    sections: [
      {
        title: "Step 1 · Personal information",
        icon: "mdi-account-outline",
        fields: [
          { label: "Full name (English)", required: true },
          { label: "Full name (Somali)", required: true },
          { label: "Nationality", required: true },
          { label: "Gender", required: true },
          { label: "Date of birth", required: true },
          { label: "National ID number", required: true },
          { label: "Passport number", required: false },
        ],
      },
      {
        title: "Step 2 · Location & contact",
        icon: "mdi-map-marker-outline",
        fields: [
          { label: "Federal member state", required: true },
          { label: "Region", required: true },
          { label: "District", required: true },
          { label: "Street / area", required: true },
          { label: "Phone number", required: true },
          { label: "Email address", required: true },
          { label: "Alternate phone", required: false },
          { label: "Postal / physical address", required: true },
        ],
      },
      {
        title: "Step 3 · Qualifications & experience",
        icon: "mdi-briefcase-outline",
        fields: [
          { label: "Highest degree / diploma", required: true },
          { label: "Field of study", required: true },
          { label: "Institution / university attended", required: true },
          { label: "Graduation year", required: true },
          {
            label:
              "Primary specialization (electrical, ICT, hospitality, etc.)",
            required: true,
          },
          {
            label:
              "Trainer level (junior, certified, senior, master, assessor, verifier)",
            required: true,
          },
          { label: "Current employer", required: false },
          { label: "Years of TVET experience", required: true },
          { label: "Additional certifications", required: false },
          {
            label: "Professional bio (training background & expertise)",
            required: true,
          },
        ],
      },
    ],
    documents: [
      { label: "Passport-size photo", required: true },
      { label: "National ID / passport scan", required: true },
      { label: "Curriculum vitae (CV)", required: true },
      { label: "Education qualification certificate", required: true },
      { label: "Declaration of accuracy", required: true },
    ],
  },
];

const otherServices = [
  {
    id: 3,
    title: "Course Accreditation",
    icon: "mdi-book-open-variant",
    description:
      "Rigorous evaluation of individual training programmes to ensure they meet national occupational standards and industry needs.",
    features: [
      "Trainer qualification review",
      "Equipment & lab inspection",
      "Quality management systems",
    ],
  },
  {
    id: 4,
    title: "Certification Issuance",
    icon: "mdi-certificate",
    description:
      "Official issuance of nationally recognized certificates to graduates of accredited programmes.",
    features: [
      "Digital credentialing",
      "QR code verification",
      "International equivalency",
    ],
  },
];
</script>

<style scoped lang="scss">
.accreditation-card {
  width: 100%;
  background: rgb(var(--v-theme-surface));
  border: 1px solid rgba(0, 0, 0, 0.05);
  transition: all 0.3s cubic-bezier(0.2, 0, 0, 1);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 20px 35px -12px rgba(0, 0, 0, 0.1) !important;
  }
}

.card-header {
  position: relative;
  padding: 20px 28px 0 28px;
  overflow: hidden;
}

.header-decoration {
  position: absolute;
  top: -30px;
  right: -30px;
  width: 150px;
  height: 150px;
  background: rgb(var(--v-theme-info));
  border-radius: 50%;
  pointer-events: none;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}

.badge-wrapper {
  display: flex;
  align-items: center;
  gap: 12px;
  background: rgba(33, 150, 243, 0.08);
  padding: 8px 20px;
  border-radius: 100px;
  backdrop-filter: blur(2px);
}

.badge-text {
  font-size: 0.85rem;
  font-weight: 600;
  letter-spacing: 0.3px;
  color: #1a5f9e;
  text-transform: uppercase;
}

.accent-icon {
  opacity: 0.7;
}

.card-title {
  font-size: 2rem !important;
  font-weight: 700 !important;
  line-height: 1.2 !important;
  margin-top: 16px;
  margin-bottom: 8px;
  color: #0b2b3f;
  position: relative;
  padding-bottom: 12px;
}

.title-underline {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 60px;
  height: 4px;
  background: rgb(var(--v-theme-info));
  border-radius: 4px;
}

.card-subtitle {
  font-size: 1rem;
  color: #5a6e7c;
  margin-top: 8px;
  line-height: 1.5;
}

.registration-container {
  margin-top: 32px !important;
}

.registration-col {
  padding: 12px !important;
}

.registration-card {
  position: relative;
  overflow: hidden;
  padding: 28px;
  border-radius: 28px;
  background: #ffffff;
  border: 1px solid #e8eef5;
  height: 100%;
  transition: all 0.3s ease;

  &:hover {
    border-color: #c5d9f0;
    box-shadow: 0 16px 40px rgba(15, 23, 42, 0.08);
  }

  &--indigo {
    border-top: 4px solid #3949ab;

    .registration-card__glow {
      background: transparent;
    }
  }

  &--teal {
    border-top: 4px solid #00897b;

    .registration-card__glow {
      background: transparent;
    }
  }
}

.registration-card__glow {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.registration-card__header {
  position: relative;
  display: flex;
  gap: 20px;
  align-items: flex-start;
  margin-bottom: 24px;
}

.registration-card__icon {
  flex-shrink: 0;
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f4f7fc;
  border-radius: 20px;
}

.registration-card__intro {
  flex: 1;
  min-width: 0;
}

.registration-card__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 10px;
}

.registration-card__title {
  font-size: 1.45rem;
  font-weight: 700;
  color: #0f2438;
  margin: 0 0 8px;
  line-height: 1.25;
}

.registration-card__description {
  font-size: 0.95rem;
  line-height: 1.6;
  color: #5c6f87;
  margin: 0;
}

.registration-card__steps {
  position: relative;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 24px;
  padding-bottom: 24px;
  border-bottom: 1px dashed #e2e8f0;
}

.registration-card__step-pill {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 14px 6px 8px;
  background: #f8fafd;
  border: 1px solid #e8eef5;
  border-radius: 100px;
  font-size: 0.78rem;
  font-weight: 600;
  color: #3d5a73;
}

.registration-card__step-num {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #ffffff;
  border: 1px solid #d6e2ef;
  font-size: 0.7rem;
  font-weight: 700;
  color: #1976d2;
}

.registration-card__sections {
  position: relative;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
}

.requirement-section {
  background: #f9fbfe;
  border: 1px solid #edf2f8;
  border-radius: 18px;
  padding: 16px 18px;
}

.requirement-section__head {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
}

.requirement-section__title {
  font-size: 0.88rem;
  font-weight: 700;
  color: #1e3348;
  margin: 0;
  line-height: 1.3;
}

.requirement-section__list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.requirement-section__item {
  display: flex;
  align-items: flex-start;
  gap: 4px;
  padding: 5px 0;
  font-size: 0.82rem;
  line-height: 1.4;
}

.requirement-section__bullet {
  flex-shrink: 0;
  margin-top: 1px;
}

.requirement-section__label {
  flex: 1;
  color: #4a627a;
}

.requirement-section__chip {
  flex-shrink: 0;
  margin-left: 4px;
  font-size: 0.65rem !important;
  height: 20px !important;
}

.registration-card__documents {
  position: relative;
  background: rgb(var(--v-theme-surface));
  border: 1px solid #e6edf5;
  border-radius: 18px;
  padding: 18px 20px;
  margin-bottom: 24px;
}

.registration-card__documents-head {
  display: flex;
  align-items: center;
  font-size: 0.88rem;
  font-weight: 700;
  color: #1e3348;
  margin-bottom: 12px;
}

.registration-card__doc-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.registration-card__doc-chip {
  font-size: 0.78rem !important;
}

.registration-card__file-note {
  display: flex;
  align-items: flex-start;
  margin: 14px 0 0;
  font-size: 0.78rem;
  line-height: 1.5;
  color: #6b829c;
}

.registration-card__footer {
  position: relative;
  display: flex;
  justify-content: flex-end;
}

.registration-card__cta {
  font-weight: 600;
  text-transform: none;
  letter-spacing: 0.2px;
}

.services-kicker {
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: #6c86a3;
  margin: 8px 12px 16px;
}

.services-container {
  margin-top: 16px !important;
}

.service-col {
  padding: 12px !important;
}

.service-item {
  display: flex;
  gap: 18px;
  padding: 20px;
  background: #ffffff;
  border-radius: 24px;
  transition: all 0.25s ease;
  border: 1px solid #eef2f6;
  height: 100%;

  &:hover {
    border-color: #bbdef5;
    background: #fefefe;
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.02);
  }
}

.service-icon-wrapper {
  flex-shrink: 0;
  width: 52px;
  height: 52px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #eef3fc;
  border-radius: 18px;
  transition: 0.2s;
}

.service-item:hover .service-icon-wrapper {
  background: #1976d2;

  :deep(.v-icon) {
    color: white !important;
  }
}

.service-content {
  flex: 1;
}

.service-title {
  font-size: 1.2rem;
  font-weight: 600;
  margin: 0 0 8px 0;
  color: #1e2f3e;
}

.service-description {
  font-size: 0.9rem;
  line-height: 1.5;
  color: #5c6f87;
  margin-bottom: 12px;
}

.service-features {
  list-style: none;
  padding: 0;
  margin: 8px 0 0 0;

  li {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 0.8rem;
    color: #3d5a73;
    margin-bottom: 6px;

    span {
      color: #4a627a;
    }
  }
}

.stats-row {
  display: flex;
  align-items: center;
  justify-content: space-around;
  background: #f8fafd;
  border-radius: 60px;
  margin: 32px 0 8px 0;
  padding: 16px 20px;
  flex-wrap: wrap;
  gap: 16px;
}

.stat-item {
  text-align: center;
  flex: 1;
  min-width: 100px;
}

.stat-value {
  font-size: 1.6rem;
  font-weight: 800;
  color: #1976d2;
  line-height: 1.2;
}

.stat-label {
  font-size: 0.75rem;
  font-weight: 500;
  color: #6c86a3;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.stat-divider {
  width: 1px;
  height: 30px;
  background: #e2e8f0;
}

.card-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 20px;
  padding: 20px 28px 28px 28px;
}

.card-actions__buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.action-left {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.deadline-text {
  font-size: 0.8rem;
  color: #788aa7;
  font-weight: 500;
}

@media (max-width: 700px) {
  .card-title {
    font-size: 1.6rem !important;
  }

  .registration-card {
    padding: 20px;
  }

  .registration-card__header {
    flex-direction: column;
    gap: 14px;
  }

  .registration-card__sections {
    grid-template-columns: 1fr;
  }

  .registration-card__footer {
    justify-content: stretch;

    .registration-card__cta {
      width: 100%;
    }
  }

  .service-item {
    flex-direction: column;
    align-items: flex-start;
  }

  .stats-row {
    flex-direction: column;
    background: transparent;
    padding: 16px 0;
    gap: 12px;

    .stat-divider {
      display: none;
    }

    .stat-item {
      background: #f8fafd;
      width: 100%;
      padding: 12px;
      border-radius: 40px;
    }
  }

  .card-actions {
    flex-direction: column;
    align-items: stretch;

    .action-left {
      justify-content: center;
    }

    .card-actions__buttons {
      flex-direction: column;
      width: 100%;

      .v-btn {
        width: 100%;
      }
    }
  }
}
</style>
